/**
 * Cidara 18+ High Risk Flu Prevention Study - Self-Contained Module
 *
 * This module contains all configuration for the Cidara 18+ High Risk Flu Prevention Study
 * in a single, easily maintainable location.
 */

import { defineStudy } from "./registry";
import { cidaraEligibleZipCodes } from "../../../data/vaccine-study-zipcodes/cidaraZipCodes";
import { cidara18HighRiskScreeningQuestions } from "../../../data/pre-screener-config/Cidara18HighRiskScreener";

/**
 * Common question IDs that will be asked before zipcode
 * and should be filtered out of study-specific questions
 */
const COMMON_QUESTION_IDS = ["age_eligibility", "past_vaccine_study_participation"];

/**
 * Cidara 18+ High Risk Flu Prevention Study
 *
 * Priority: 3 (Third priority - after Cidara 65+ and Seqirus)
 * Trial ID: VFLUSQ306
 * Location: Same zipcodes as Cidara 65+ study
 *
 * Key criteria:
 * - Age 18+
 * - At least ONE high-risk condition (COPD, asthma with preventer, CF, heart conditions, diabetes on insulin, CKD, immunocompromised, etc.)
 * - BMI >= 18
 * - Weight >= 40kg
 * - No recent influenza diagnosis (past 5 months)
 * - No major exclusionary conditions
 */
export const cidara18HighRiskStudy = defineStudy({
  id: "cidara-18-high-risk",
  name: "Cidara Flu Prevention Study for Adults at Higher Risk",
  trialId: "VFLUSQ306",
  priority: 3,

  // Convert readonly array to regular array for compatibility
  // Uses same zipcode list as Cidara 65+ (cohort of same study)
  eligibleZipCodes: Array.from(cidaraEligibleZipCodes),

  // All questions including common ones (will be filtered automatically)
  questions: cidara18HighRiskScreeningQuestions,

  // Questions to filter out (already asked in common stage)
  commonQuestionIds: COMMON_QUESTION_IDS,

  // Required common answer values (age 18+)
  commonAnswerRequirements: {
    age_eligibility: ["18_to_49", "50_to_64", "65_or_older"],
  },

  metadata: {
    sponsor: "Cidara Therapeutics",
    description:
      "Investigating an investigational medication designed to prevent influenza in adults aged 18+ who are at higher risk of flu complications",
  },
});
